// Track my booking (1NOW-11157): the pure parts of the page and the menu link,
// kept apart so they can be tested without a renderer.

export const TRACK_BOOKING_PATH = "/track-booking";
export const TRACK_BOOKING_LABEL = "Track my booking";

/** One row of POST /api/bookings/track/verify/ (bookings/track.py). */
export interface TrackedBooking {
  unique_identifier: string;
  booking_number: number;
  car_name: string;
  pick_up_time: string;
  drop_off_time: string;
  timezone: string | null;
  status: "confirmed" | "payment_due" | "cancellation_requested" | "change_requested" | "completed" | "ended";
  status_label: string;
}

/**
 * The header menu: the operator's items, with Track my booking last. Left alone when the operator
 * emptied the menu on purpose, when it has not loaded yet, and when they already
 * link to the page themselves.
 */
export const withTrackBookingItem = (items: any, cleared?: boolean): any[] | undefined => {
  if (!Array.isArray(items) || cleared === true) return Array.isArray(items) ? items : undefined;
  const urlOf = (item: any) => String(item?.url ?? item?.href ?? "").split(/[?#]/)[0].replace(/\/+$/, "");
  if (items.some((item) => urlOf(item) === TRACK_BOOKING_PATH)) return items;
  const link = { id: "track-booking", name: TRACK_BOOKING_LABEL, url: TRACK_BOOKING_PATH };
  // Always last in the menu (Taha, 6 Oct).
  return [...items, link];
};

export const CODE_LENGTH = 6;
/** How long a renter waits before asking for another code. */
export const RESEND_AFTER_SECONDS = 60;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isEmail = (value: string) => EMAIL.test(value.trim());

/** "sara.khan@email.com" as "s***@email.com": enough to tell the renter which inbox, not enough to read it. */
export const maskEmail = (email: string) => {
  const [local, domain] = email.trim().split("@");
  return local && domain ? `${local.charAt(0)}***@${domain}` : "";
};

/** What the code field holds: digits only, at most six, however the renter typed or pasted it. */
export const cleanCode = (value: string) => String(value ?? "").replace(/\D/g, "").slice(0, CODE_LENGTH);

const fmt = (date: Date, timeZone: string | undefined, opts: Intl.DateTimeFormatOptions) => {
  try {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone }).format(date);
  } catch {
    // An unknown zone name: show the date in the browser's own zone.
    return new Intl.DateTimeFormat("en-US", opts).format(date);
  }
};

/** The trip's days in the booking's own time zone: "Oct 10 – Oct 13" and "Fri, Oct 10 → Mon, Oct 13". */
export const tripDays = (pickUp: string, dropOff: string, timeZone?: string | null) => {
  const from = new Date(pickUp);
  const to = new Date(dropOff);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return { short: "", long: "" };
  const zone = timeZone || undefined;
  const short = { month: "short", day: "numeric" } as const;
  const long = { weekday: "short", month: "short", day: "numeric" } as const;
  return {
    short: `${fmt(from, zone, short)} – ${fmt(to, zone, short)}`,
    long: `${fmt(from, zone, long)} → ${fmt(to, zone, long)}`,
  };
};

/**
 * Green for a booking that needs nothing, amber for one that is waiting on someone, grey for a
 * trip that is over (completed, or ended and never closed by the host).
 */
export const statusTone = (status: string): "green" | "amber" | "gray" =>
  status === "confirmed" ? "green" : status === "completed" || status === "ended" ? "gray" : "amber";

/**
 * 1NOW-11157 (Faiza, 8 Oct): the verified list, kept for this tab, so Back from a booking shows it again instead of
 * asking for a new code (behind a 5 to 60 minute wait, the used code spent). The verify call hands back only the
 * list, no token to re-fetch it with, so the list itself is kept: in sessionStorage (this tab only, gone when it
 * closes), one entry per company, never in the URL. "Use a different email" and a new code forget it.
 */
export const TRACKED_FOR_MS = 30 * 60 * 1000;
export interface TrackedResult {
  email: string;
  bookings: TrackedBooking[];
  at: number;
}
const trackedKey = (companyId: unknown) => `track_booking_result:${companyId}`;

export const saveTracked = (companyId: unknown, email: string, bookings: TrackedBooking[]) => {
  try {
    sessionStorage.setItem(trackedKey(companyId), JSON.stringify({ email, bookings, at: Date.now() }));
  } catch {
    // private mode or full: Back asks for a code, as before
  }
};

export const forgetTracked = (companyId: unknown) => {
  try {
    sessionStorage.removeItem(trackedKey(companyId));
  } catch {
    // nothing kept
  }
};

/** The kept list while it is under TRACKED_FOR_MS old; an older or unreadable one is dropped. */
export const readTracked = (companyId: unknown): TrackedResult | null => {
  try {
    const kept = JSON.parse(sessionStorage.getItem(trackedKey(companyId)) ?? "null");
    if (kept && Array.isArray(kept.bookings) && Date.now() - Number(kept.at) < TRACKED_FOR_MS) return kept;
  } catch {
    // unreadable: forget it below
  }
  forgetTracked(companyId);
  return null;
};
